// Near-miss cues: the supersonic crack of a round passing within feet, and a
// CHECK SIX threat accumulator that trips when someone behind settles their
// guns on you. Pure math over snapshot bullet rows — plain {x,y,z} vectors in,
// side-effect requests out — so main.js owns all audio/HUD consequences and
// this stays trivially unit-testable.
//
// Bullets carry no IDs on the wire, so per-bullet dedupe is impossible; the
// sweep instead runs once per snapshot over the 1/snapshotRate window each
// row covers (a fast round is only within crack range for well under one
// window) and a token bucket caps the crack rate for the straddled-by-flak
// case, where the same slow rows would otherwise crack every snapshot.

const CRACK_MIN_SPEED = 250; // m/s: subsonic lobs don't crack
const CRACKS_PER_SEC = 6;

export class NearMiss {
  constructor(cfg = {}) {
    this.crackRadiusM = cfg.crackRadiusM ?? 8;
    this.coneRad = cfg.checkSixConeRad ?? 0.15;
    this.triggerSec = cfg.checkSixTriggerSec ?? 0.25;
    this.cooldownSec = cfg.stingCooldownSec ?? 6;
    this.threat = 0;
    this._tokens = CRACKS_PER_SEC;
    this._stingAt = -Infinity;
  }

  /**
   * Sweep one snapshot's bullet rows. Call once per received snapshot.
   *
   * rows      snapshot bl rows: [x,y,z,vx,vy,vz,team,...]
   * selfTeam  local player's team
   * listener  {x,y,z} camera position (the listening ear)
   * right     {x,y,z} camera right unit vector (stereo axis)
   * fwd       {x,y,z} self forward unit vector, or null to skip
   *           check-six accumulation (manned guns crack but have no six)
   * dtSnap    seconds this snapshot covers (1 / snapshotRateHz)
   * now       seconds, any monotonic clock
   *
   * Returns { cracks: [pan,...], sting: boolean } — pans in [-1, 1].
   */
  sweep(rows, selfTeam, listener, right, fwd, dtSnap, now) {
    this._tokens = Math.min(CRACKS_PER_SEC, this._tokens + CRACKS_PER_SEC * dtSnap);
    // ×0.5 per second: old pressure fades fast once the shooting stops.
    this.threat *= Math.pow(0.5, dtSnap);
    const cracks = [];
    let sting = false;
    for (const r of rows) {
      if (r[6] === selfTeam) continue;
      const vx = r[3], vy = r[4], vz = r[5];
      const speed = Math.hypot(vx, vy, vz);
      if (speed < CRACK_MIN_SPEED) continue;

      // Closest approach of the segment this row flies over the window.
      const ax = r[0] - listener.x, ay = r[1] - listener.y, az = r[2] - listener.z;
      const sx = vx * dtSnap, sy = vy * dtSnap, sz = vz * dtSnap;
      const segLen2 = sx * sx + sy * sy + sz * sz;
      let t = segLen2 > 0 ? -(ax * sx + ay * sy + az * sz) / segLen2 : 0;
      t = Math.max(0, Math.min(1, t));
      const cx = ax + sx * t, cy = ay + sy * t, cz = az + sz * t;
      const dist = Math.hypot(cx, cy, cz);

      if (dist < this.crackRadiusM && this._tokens >= 1) {
        this._tokens -= 1;
        const inv = dist > 1e-6 ? 1 / dist : 0;
        const pan = Math.max(-1, Math.min(1,
          (cx * right.x + cy * right.y + cz * right.z) * inv));
        cracks.push(pan);
      }

      // Check six: the round is aimed at us (velocity within the cone of the
      // bullet→self bearing) and flying our way (rear-hemisphere origin).
      if (fwd) {
        const toSelfLen = Math.hypot(ax, ay, az);
        if (toSelfLen > 1e-6) {
          // ax.. points self→bullet; the bullet→self bearing is its negation.
          const dot = -(vx * ax + vy * ay + vz * az) / (speed * toSelfLen);
          const aimed = dot > Math.cos(this.coneRad);
          const fromBehind = (vx * fwd.x + vy * fwd.y + vz * fwd.z) / speed > 0.5;
          if (aimed && fromBehind) this.threat += dtSnap;
        }
      }
    }
    if (this.threat > this.triggerSec && now - this._stingAt > this.cooldownSec) {
      this._stingAt = now;
      this.threat = 0;
      sting = true;
    }
    return { cracks, sting };
  }
}
